import React, { useEffect, useRef, useState } from "react";
import "./DSPlayer.css";
import { PlaylistItemDTO } from "../lib/types";
import { DSPlayerVideo } from "./playType/DSPlayerVideo";
import { DSPlayerImage } from "./playType/DSPlayerImage";
import { DSPlayerDebugView } from "./DSPlayerDebugView";
import { DSPlayerAudio } from "./playType/DSPlayerAudio";
import { DSPlayerSplit } from "./playType/DSPlayerSplit";
import { DSPlayerLink } from "./playType/DSPlayerLink";
import { DSPlayerDoc } from "./playType/DSPlayerDoc";
import { sendPlayerStat } from "../lib/dsApi";

const SEND_STATS_INTERVAL_MS = 1000 * 15;

type Props = {
  list: PlaylistItemDTO[] | null;
  debug?: boolean;
  isFullScreen?: boolean;
};

export const DSPlayer: React.FC<Props> = ({ list, debug, isFullScreen }) => {
  const localPlaylist = list || [];
  const [currentIndex, setCurrentIndex] = useState<number>(-1);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  // Used for link, image and split only as video and audio are handled by the `onTimeUpdate`
  const durationTimerRef = useRef<number | null>(null);
  const playingItemLoggerRef = useRef<number | null>(null);

  // Function to update volume for all media elements
  const updateMediaVolume = () => {
    const savedVolume = localStorage.getItem("dsVolume");
    const volume = savedVolume ? parseInt(savedVolume, 10) / 100 : 1;

    // Update all audio elements
    document.querySelectorAll("audio").forEach((audio) => {
      audio.volume = volume;
    });

    // Update all video elements
    document.querySelectorAll("video").forEach((video) => {
      video.volume = volume;
      if (hasUserInteracted) {
        video.muted = false;
      }
    });
  };

  // Handle user interaction
  useEffect(() => {
    const handleInteraction = () => {
      setHasUserInteracted(true);
      updateMediaVolume();
    };

    window.addEventListener("click", handleInteraction);
    window.addEventListener("touchstart", handleInteraction);
    window.addEventListener("keydown", handleInteraction);

    return () => {
      window.removeEventListener("click", handleInteraction);
      window.removeEventListener("touchstart", handleInteraction);
      window.removeEventListener("keydown", handleInteraction);
    };
  }, [hasUserInteracted]);

  useEffect(() => {
    setCurrentIndex(0);
    document.body.classList.add("ds-player");
    return () => {
      clearImageTimer();
      document.body.classList.remove("ds-player");
    };
  }, [list, setCurrentIndex]);

  useEffect(() => {
    if (currentIndex === -1) return;
    try {
      startPlayback(currentIndex);
    } catch (error) {
      console.error("error startPlayback", { error });
    }
  }, [currentIndex]);

  const startPlayback = (index: number): void => {
    clearImageTimer();

    const item = localPlaylist[index];
    if (!item) {
      // console.log("item not found for the given index", {
      //   index,
      //   localPlaylist,
      // });
      return;
    }

    if (item.type === "Video") {
      playVideo(item.id);
    } else if (item.type === "Audio") {
      playAudio(item.id);
    } else if (item.type === "Image") {
      // Used for link, image and split only as others
      durationTimerRef.current = window.setTimeout(() => handleContentEnd(index), item.duration * 1000);
    } else if (item.type === "Split") {
      // Used for link, image and split only as others
      durationTimerRef.current = window.setTimeout(() => handleContentEnd(index), item.duration * 1000);
    } else if (item.type === "Link") {
      // Used for link, image and split only as others
      durationTimerRef.current = window.setTimeout(() => handleContentEnd(index), item.duration * 1000);
    }
  };

  const playVideo = async (id: string | number): Promise<void> => {
    const video = document.getElementById(id.toString()) as HTMLVideoElement;
    if (video) {
      try {
        video.currentTime = 0;
        // Initially play muted to avoid autoplay restrictions
        video.muted = !hasUserInteracted;
        await video.play();
      } catch (error) {
        console.error("Video playback error:", error);
        // If play fails, try again with muted
        video.muted = true;
        await video.play();
      }
    }
  };

  const playAudio = async (id: string | number): Promise<void> => {
    const audio = document.getElementById(id.toString()) as HTMLAudioElement;
    if (audio) {
      try {
        audio.currentTime = 0;
        // Only play audio if user has interacted
        if (hasUserInteracted) {
          await audio.play();
        }
      } catch (error) {
        console.error("Audio playback error:", error);
      }
    }
  };

  const handleContentEnd = (currentIndex: number): void => {
    const nextIndex = (currentIndex + 1) % localPlaylist.length;

    // If the next index is the same as the current index and the next index is 0, play the video right after it ends
    // This is the case when we only have 1 item in the playlist, which is a video
    if (nextIndex === currentIndex && nextIndex === 0) {
      const item = localPlaylist[nextIndex];
      if (item && item.type === "Video") {
        playVideo(item.id);
      }
    } else {
      setCurrentIndex(nextIndex);
    }
  };

  const clearImageTimer = (): void => {
    // Used for link, image and split only as others
    if (durationTimerRef.current !== null) {
      // Used for link, image and split only as others
      clearTimeout(durationTimerRef.current);
      // Used for link, image and split only as others
      durationTimerRef.current = null;
    }
  };

  const playingItem = localPlaylist[currentIndex];

  useEffect(() => {
    playingItemLoggerRef.current = window.setInterval(() => {
      const playingContentId = playingItem?.intId;
      if (playingContentId) {
        const registeredCode = localStorage.getItem("Code");
        const uniqueId = localStorage.getItem("UniqueId");
        if (registeredCode && uniqueId) {
          try {
            sendPlayerStat({
              uniqueId,
              code: registeredCode,
              contentId: playingContentId,
            });
          } catch (error) {
            console.error("error sending player stat", { error });
          }
        }
      }
    }, SEND_STATS_INTERVAL_MS);

    return () => {
      if (playingItemLoggerRef.current !== null) {
        clearInterval(playingItemLoggerRef.current);
        playingItemLoggerRef.current = null;
      }
    };
  }, [playingItem]);

  useEffect(() => {
    if (playingItem) {
      const playingContentId = playingItem?.intId;
      if (playingContentId) {
        const registeredCode = localStorage.getItem("Code");
        const uniqueId = localStorage.getItem("UniqueId");
        if (registeredCode && uniqueId) {
          try {
            sendPlayerStat({
              uniqueId,
              code: registeredCode,
              contentId: playingContentId,
            });
          } catch (error) {
            console.error("error sending player stat", { error });
          }
        }
      }
    }
  }, [playingItem]);

  useEffect(() => {
    document.body.style.cursor = "none";

    setInterval(() => {
      // Every 10 seconds, set the cursor to none to force the browser to hide the cursor because of some old Browsers keep showing
      document.body.style.cursor = "none";
    }, 1000 * 10);

    return () => {
      document.body.style.cursor = "auto";
    };
  }, []);

  return (
    <div style={{ overflow: "hidden" }} className="cursor-none">
      {debug && <DSPlayerDebugView currentIndex={currentIndex} playingItem={playingItem} />}
      <div style={{ overflow: "hidden" }}>
        {localPlaylist.map((item, index) => {
          const isVisible = index === currentIndex;
          return (
            <div
              key={item.id}
              style={{
                overflow: "hidden",
                display: isVisible ? "block" : "none",
              }}
            >
              {item.type === "Video" && (
                <DSPlayerVideo item={item} onVideoEnded={() => handleContentEnd(index)} isFullScreen={isFullScreen} />
              )}
              {item.type === "Image" && <DSPlayerImage item={item} isFullScreen={isFullScreen} />}
              {item.type === "Audio" && (
                <DSPlayerAudio item={item} onAudioEnded={() => handleContentEnd(index)} isFullScreen={isFullScreen} />
              )}
              {item.type === "Split" && <DSPlayerSplit item={item} isFullScreen={isFullScreen} />}
              {item.type === "Link" && <DSPlayerLink item={item} isFullScreen={isFullScreen} />}
              {item.type === "Doc" && <DSPlayerDoc item={item} isFullScreen={isFullScreen} />}
            </div>
          );
        })}
      </div>
    </div>
  );
};
